import { Navigate } from 'react-router-dom';
import { useUserRole, UserRole, getRedirectPathForRole } from '@/hooks/useUserRole';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

/**
 * Generic protected route component that checks user role.
 * Redirects to appropriate page based on role if not allowed.
 */
export function ProtectedRoute({ 
  children, 
  allowedRoles, 
  fallbackPath 
}: ProtectedRouteProps) {
  const { role, loading, isAuthenticated } = useUserRole();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated - redirect to auth
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user's role is allowed
  if (!allowedRoles.includes(role)) {
    // Use fallback path or redirect based on actual role
    const redirectTo = fallbackPath || getRedirectPathForRole(role);
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Admin-only route protection.
 * Non-admins are redirected to their appropriate area.
 */
export function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['admin']}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Professional route protection.
 * Allows professionals and admins. Consumers are redirected to /perfil.
 */
export function ProtectedProfessionalRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute 
      allowedRoles={['professional', 'admin']} 
      fallbackPath="/perfil"
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * Consumer route protection.
 * Professionals are redirected to /profissional.
 */
export function ProtectedConsumerRoute({ children }: { children: React.ReactNode }) {
  const { role, loading, isAuthenticated } = useUserRole();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Professionals should go to their area
  if (role === 'professional') {
    return <Navigate to="/profissional" replace />;
  }

  // Admins can access consumer routes (they can also access admin routes)
  return <>{children}</>;
}
